'use client';

/**
 * components/blocks/experience-slot/experience-slot.tsx
 *
 * Block-agnostic wrapper around Ninetailed's `<Experience>`. It knows nothing
 * about banners (no `mainBanner*` field) so any block can reuse it: give it a
 * presentational `component`, a `baseline` object (the entry's own fields, incl.
 * its `id`), and the raw Contentful `experiences` collection. Personalization
 * concerns (mapping, baseline-first swap, tracking) live here; the wrapped
 * component stays a plain presentational component.
 *
 * Baseline-first by contract: `<Experience>` renders the baseline until the
 * visitor profile resolves, then swaps to the winning variant. `mapExperiences`
 * returns `[]` for any missing/malformed data, so the baseline simply renders
 * unchanged — a zero-regression fallback. `passthroughProps` are forwarded to
 * the component; on a key collision the library lets a variant field take
 * precedence over the passthrough value (`{...passthroughProps, ...variant}`),
 * so reserve passthrough keys (locale / carousel state) that variants never
 * author.
 *
 * When Ninetailed is not active for the tenant (no client key → no provider),
 * this renders the plain `component` with the baseline directly: `<Experience>`
 * calls `useNinetailed()` unconditionally and would otherwise throw.
 *
 * Consent is handled centrally by the SDK's privacy plugin (see
 * buildNinetailedOptions), which blocks ALL events — page, identify, and the
 * component impression/click events this slot produces — until the visitor grants
 * consent. So this always renders `<Experience trackClicks>` and always injects
 * `ninetailedTrack`; every event is dropped by the plugin pre-consent. Because no
 * profile is fetched before consent, `<Experience>` simply shows the baseline
 * until consent is granted.
 */

import React from 'react';
import { Experience, useNinetailed } from '@ninetailed/experience.js-next';

import { mapExperiences } from '@/lib/ninetailed/mapExperiences';
import { resolveMemberVariant } from '@/lib/ninetailed/resolveMemberVariant';
import { useIsNinetailedActive } from '@/providers/ninetailed/ninetailed-provider';
import { useServerLoggedInHint } from '@/providers/ServerAuthHintProvider';
import { usePersonalizationConsent } from '@/lib/consent/use-personalization-consent';

/** Extra props Ninetailed injects into the wrapped component at render time. */
export interface NinetailedComponentProps {
  ninetailed?: {
    isPersonalized: boolean;
    audience: { id: string };
  };
  /**
   * Bound Ninetailed `track` for firing custom events (e.g. a `ctr` click event)
   * from the wrapped component. Only present on the SDK-active path; `undefined`
   * on the inert/baseline path, so components MUST call it optionally.
   */
  ninetailedTrack?: (
    event: string,
    properties?: Record<string, unknown>,
  ) => void;
}

export interface ExperienceSlotProps<Baseline extends { id: string }> {
  /** Presentational component rendered for the baseline and every variant. */
  component: React.ComponentType<Partial<Baseline> & NinetailedComponentProps>;
  /** The entry's own fields (must include `id`); rendered when no variant wins. */
  baseline: Baseline;
  /** Raw Contentful experiences collection (or array); safe to pass undefined. */
  experiences: unknown;
  /** Stable context forwarded to the component, never overridden by a variant. */
  passthroughProps?: Record<string, unknown>;
}

export function ExperienceSlot<Baseline extends { id: string }>({
  component: Component,
  baseline,
  experiences,
  passthroughProps,
}: ExperienceSlotProps<Baseline>) {
  const isNinetailedActive = useIsNinetailedActive();

  // Server-known login state (from the `is_logged_in` cookie, present in the
  // initial HTML). Used by the active path below to paint the member variant for
  // a logged-in visitor during the profile-resolution window — server and first
  // client render agree on this value, so there is no hydration mismatch.
  const isMemberHint = useServerLoggedInHint();

  // Only the SDK-active check gates rendering here: when the SDK is not mounted
  // for this tenant, <Experience> would throw (it calls useNinetailed()
  // unconditionally), so render the plain baseline. Consent does NOT gate
  // rendering — personalization always resolves; consent gates metrics only,
  // inside ActiveExperienceSlot.
  if (!isNinetailedActive) {
    const baselineProps = {
      ...baseline,
      ...passthroughProps,
    } as Partial<Baseline> & NinetailedComponentProps;
    return <Component {...baselineProps} />;
  }

  // Active path lives in a child component so `useNinetailed()` (and any other
  // SDK hook) is only ever called when a NinetailedProvider is guaranteed to be
  // in the tree — keeping the rules-of-hooks contract intact across the gate.
  return (
    <ActiveExperienceSlot
      component={Component}
      baseline={baseline}
      experiences={experiences}
      passthroughProps={passthroughProps}
      isMemberHint={isMemberHint}
    />
  );
}

function ActiveExperienceSlot<Baseline extends { id: string }>({
  component: Component,
  baseline,
  experiences,
  passthroughProps,
  isMemberHint,
}: ExperienceSlotProps<Baseline> & { isMemberHint: boolean }) {
  // Safe here: this component only renders on the SDK-active path (see gate in
  // ExperienceSlot), so the provider is always present.
  const ninetailed = useNinetailed();

  // The member-variant preview below is personalization, so it is gated on the
  // SAME consent as every other personalization signal (deny-by-default, reactive
  // to live OneTrust changes). Without this a logged-in visitor who REVOKES
  // consent would keep seeing the member card: revoking remounts the SDK and
  // blocks the profile fetch, so <Experience> stays on the loading component
  // indefinitely — which must therefore fall back to the baseline once consent is
  // gone.
  const hasConsent = usePersonalizationConsent();

  // Generic, block-agnostic bound tracker injected into the wrapped component so
  // it can emit custom events (e.g. the banner's `ctr` click event). Fire-and-
  // forget: the SDK's `track` returns a promise we intentionally do not await.
  // Consent is enforced centrally by the privacy plugin, which drops the event
  // pre-consent — so this can always be injected.
  const ninetailedTrack = React.useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      void ninetailed.track(
        event,
        properties as Parameters<typeof ninetailed.track>[1],
      );
    },
    [ninetailed],
  );

  const mergedPassthrough = {
    ...passthroughProps,
    ninetailedTrack,
  };

  // Ninetailed's <Experience> is typed with deep conditional generics that don't
  // infer cleanly from our block-agnostic props. Our public API above stays
  // strongly typed; we cast the library component to the concrete prop shape.
  const ExperienceTyped = Experience as unknown as React.FC<
    {
      component: React.ComponentType<
        Partial<Baseline> & NinetailedComponentProps
      >;
      experiences: ReturnType<typeof mapExperiences>;
      passthroughProps?: Record<string, unknown>;
      loadingComponent?: React.ComponentType<Record<string, unknown>>;
      trackClicks?: boolean;
    } & Baseline
  >;

  // Render the baseline VISIBLY while the profile resolves (baseline-first AC).
  // The SDK default wraps the baseline in `visibility:hidden` until the profile
  // loads or `requestTimeout` (5s) elapses, which would hide an LCP hero banner
  // on first paint; here the baseline paints immediately and the variant swaps
  // in post-resolution.
  //
  // Close over the outer `baseline` + `mergedPassthrough` (identical to the inert
  // no-key path, plus the injected tracker) rather than reconstructing them from
  // the props the SDK forwards to `loadingComponent`. This makes the pre-
  // resolution hero deterministic — it never depends on whether/what
  // `<Experience>` forwards, so the CTA slug and `isActive` animation state are
  // always correct during the loading window.
  const BaselineLoading = () => {
    // For a known member (server login hint) WITH personalization consent, paint
    // the member variant during the profile-resolution window instead of the
    // logged-out baseline, so a logged-in visitor never sees the baseline flash.
    // The variant content comes straight from the experiences collection already
    // in hand (no extra fetch); when there is no usable member variant, or consent
    // is absent/revoked, we fall back to the baseline. Once the profile resolves,
    // <Experience> swaps to its own resolved selection — for a member that is this
    // same variant, so nothing visibly changes.
    const memberVariant =
      isMemberHint && hasConsent
        ? (resolveMemberVariant(experiences) as Partial<Baseline> | null)
        : null;
    const merged = {
      ...(memberVariant ?? baseline),
      ...mergedPassthrough,
    } as Partial<Baseline> & NinetailedComponentProps;
    return <Component {...merged} />;
  };

  // `trackClicks` records a native Ninetailed click event attributed to the
  // rendered variant (CTR numerator). Always on: the privacy plugin blocks the
  // click event pre-consent, so no native click metric leaks before consent. It
  // is also active-scoped for free: inactive carousel slides are
  // `pointer-events-none`, so only the visible slide can be clicked. NOTE:
  // `<Experience>` also auto-tracks component-view impressions here (geometric
  // IntersectionObserver, `componentViewTrackingThreshold` dwell); those
  // `component` events are ALSO blocked by the privacy plugin pre-consent. The
  // injected `ninetailedTrack` lets the wrapped component ALSO fire a custom
  // `ctr` event on the actual CTA click (also plugin-gated).
  return (
    <ExperienceTyped
      component={Component}
      experiences={mapExperiences(experiences)}
      passthroughProps={mergedPassthrough}
      loadingComponent={BaselineLoading}
      trackClicks
      {...baseline}
    />
  );
}

export default ExperienceSlot;
